import { Popover } from 'antd';

import WithLoading from '@/lib/hoc/WithLoading/WithLoading';
import { SessionStatus } from '@/lib/types/session-status';
import { countSessionsByType } from '@/lib/utils/sessions';
import { SessionHub } from '@addmile/backend-client';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import ColumnVisibilityMenu from './ColumnVisibilityMenu/ColumnVisibilityMenu';
import Filters, { ExternalFilters } from './Filters/Filters';
import Stats from './Stats/Stats';
import { useSessionTableHandler } from './useSessionTableHandler';

interface SessionsTableProps extends ExternalFilters {
  sessions: SessionHub[];
  showStats?: boolean;
  isCoachView?: boolean;
  isLoading?: boolean;
}

const SessionsTable = ({
  sessions,
  externalFilterData,
  externalFilter,
  setExternalFilter,
  showStats,
  isLoading = false,
  isCoachView,
}: SessionsTableProps) => {
  const {
    data,
    columns,
    statusFilter,
    setStatusFilter,
    columnVisibility,
    toggleColumnVisibility,
    columnOptions,
  } = useSessionTableHandler(sessions, isCoachView);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      columnVisibility,
    },
  });

  const stats = countSessionsByType(data, SessionStatus);

  return (
    <div className="flex gap-4">
      <div className="p-4 bg-white shadow-md rounded-xl bg-white-100 max-w-screen-xl w-full overflow-x-auto">
        {showStats && <Stats stats={stats} />}

        <div className="flex justify-between items-center mb-4">
          <Filters
            statusFilter={statusFilter as SessionStatus}
            setStatusFilter={setStatusFilter}
            externalFilterData={externalFilterData}
            externalFilter={externalFilter}
            setExternalFilter={setExternalFilter}
          />

          <ColumnVisibilityMenu
            columnOptions={columnOptions}
            onToggleColumn={toggleColumnVisibility}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse">
            <thead className="bg-gray-100 text-left">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-2 text-base text-black border-b-2 border-primary-20 font-semibold"
                      style={{ minWidth: header.getSize() }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2 text-base font-medium"
                      style={{
                        minWidth: cell.column.getSize(),
                        maxWidth: cell.column.getSize() + 100,
                      }}
                    >
                      {cell.column.id === 'user' ? (
                        <Popover
                          placement="topLeft"
                          content={
                            <div className="max-w-[300px] break-words">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          }
                        >
                          <div className="truncate">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </Popover>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <WithLoading isLoading={isLoading}>
          {data.length ? (
            <div className="mt-4 flex justify-center items-center gap-3">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-2 py-2 text-base rounded-md disabled:opacity-50 cursor-pointer bg-primary-20"
              >
                <img src={'/icons/arrow-right.svg'} className="rotate-180" alt="Previous page" />
              </button>
              <span className="text-base font-semibold">
                {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-2 py-2 text-base rounded-md disabled:opacity-50 cursor-pointer bg-primary-20"
              >
                <img src={'/icons/arrow-right.svg'} alt="Next page" />
              </button>
            </div>
          ) : (
            <img src={'/icons/empty.svg'} className="m-auto" alt="No sessions" />
          )}
        </WithLoading>
      </div>
    </div>
  );
};

export default SessionsTable;
